import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { statisticsQueryKeys } from 'StatisticsModule/query/statisticsQueryKeys';
import { getTeamMemberCategoryCountsApi } from 'StatisticsModule/api/getTeamMemberCategoryCountsApi';
import { COUNT_STATISTICS_DATA_TYPE } from 'StatisticsModule/constants';
import { FetchCountStatisticsResponseData } from 'StatisticsModule/types';

/**
 * A single team's active membership counts split by employment type. These are
 * mutually exclusive (each membership has exactly one employment type), so they
 * can be used directly to prefill the pricing calculator without double-counting.
 */
export interface TeamMemberCategoryCounts {
  /** employment_type = "Member" (regular members, including role holders) */
  basicMember: number;
  /** employment_type = "Contractor (Internal)" */
  internalContractor: number;
  /** employment_type = "Contractor (External)" */
  externalContractor: number;
  /** employment_type = "Guest" */
  guest: number;
}

/**
 * Fetch a single team's member counts categorized by employment type.
 *
 * Backed by the overall count statistics endpoint scoped to one team. This is
 * the only per-team API that exposes the internal-contractor count, so it is
 * the source of truth for both the renewal calculator prefill (GLX-1842) and
 * the team About page's Internal Contractors row.
 */
export const useTeamMemberCategoryCountsQuery = ({
  teamId,
  tenant,
  realmId,
  enabled = true,
}: {
  teamId?: number;
  tenant?: string;
  realmId?: string;
  enabled?: boolean;
}) => {
  // The query only runs with a real team context. Track this explicitly so the
  // loading flag is guaranteed false when the query is disabled — a disabled
  // React Query is still in `status: 'pending'`, and consumers that pass the
  // flag straight to a loading UI (e.g. About.tsx) would otherwise risk a
  // stuck "Loading..." state.
  const isEnabled = enabled && !!teamId;

  const { data, ...query } = useAppQuery<FetchCountStatisticsResponseData>({
    queryKey: statisticsQueryKeys.detail({
      identifier: 'useTeamMemberCategoryCountsQuery',
      teamId,
      tenant,
      realmId,
    }),
    queryFn: async ({ token, buildConfig }) => {
      const response = await getTeamMemberCategoryCountsApi({
        params: { teamId: teamId as number, tenant, realmId },
        token,
        config: buildConfig(),
      });
      return response.data as FetchCountStatisticsResponseData;
    },
    enabled: isEnabled,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const filteredCounts =
    data?.count_statistics?.[COUNT_STATISTICS_DATA_TYPE.TEAM_MEMBERSHIPS]?.filtered_counts;

  const memberCategoryCounts: TeamMemberCategoryCounts | undefined = filteredCounts
    ? {
        basicMember: filteredCounts.basic_member,
        internalContractor: filteredCounts.contractor_internal,
        externalContractor: filteredCounts.contractor_external,
        guest: filteredCounts.guest,
      }
    : undefined;

  return {
    memberCategoryCounts,
    // Only ever true while an enabled query is actually fetching.
    isLoadingMemberCategoryCounts: isEnabled && query.isLoading,
    ...query,
  };
};

export default useTeamMemberCategoryCountsQuery;
